import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import classnames from 'classnames';
import { FC, useState } from 'react';
import { usePopper } from 'react-popper';

import { formatCents, PLUS_TAX_TEXT } from 'utilities/money';
import { pluralize } from 'utilities/pluralize';

type Props = {
  offer: {
    avgPrice: number;
    totalPrice: number;
    basePrice: number;
    rateCode: string;
    rateCodeCategory?: string | null;
    nights: number;
    estimatedTax: number;
  };
  thsPrice?: {
    nightlyPrice: number;
    includesTax: boolean;
  };
  brief?: boolean;
};

const PriceTag: FC<Props> = ({ offer, thsPrice, brief }) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [isShowing, setIsShowing] = useState(false);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
  });
  const { avgPrice, totalPrice, basePrice, nights, estimatedTax } = offer;

  const mainPriceText = () => {
    if (thsPrice) {
      const priceText = formatCents(thsPrice.nightlyPrice);
      const taxText = brief ? '' : ` ${PLUS_TAX_TEXT}`;
      return `${priceText}${taxText}`;
    }

    if (avgPrice) return formatCents(avgPrice);
    if (basePrice) return formatCents(basePrice);
    return `${formatCents(totalPrice)} total${brief ? '' : ' incl tax/fees'}`;
  };

  const nightlyPriceText = () => {
    const nightsText = `${nights} ${pluralize(nights, 'night')}`;

    if (thsPrice) {
      const taxContext = PLUS_TAX_TEXT;
      return `${formatCents(thsPrice.nightlyPrice)}/ni (${taxContext}) for ${nightsText}`;
    }

    if (avgPrice)
      return `${formatCents(avgPrice)}/ni (average) for ${nightsText}`;
    return nightsText;
  };

  return (
    <div className="flex h-full flex-col justify-center sm:justify-between">
      {/* Mobile: Row layout, Desktop: Column layout */}
      <div className="flex flex-row items-center justify-between gap-4 sm:flex-col sm:items-end sm:gap-0 sm:text-right">
        <div className="flex flex-col">
          <Popover
            className={classnames(
              'relative inline-flex items-center rounded py-0.5',
              { 'z-10': isShowing },
            )}
            onFocus={() => setIsShowing(true)}
            onBlur={() => setIsShowing(false)}
            onMouseEnter={() => setIsShowing(true)}
            onMouseLeave={() => setIsShowing(false)}
          >
            {/* Description */}
            <PopoverButton
              ref={setReferenceElement}
              className="text-left sm:text-right"
            >
              <p className="font-oswald text-lg font-bold text-accentGreen sm:text-xl sm:font-medium sm:text-accentGreenShade">
                {mainPriceText()}
              </p>
            </PopoverButton>

            {/* Panel */}
            {isShowing && (
              <PopoverPanel
                static
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}
              >
                <div className="-mx-2 my-2 whitespace-nowrap rounded-md bg-accentGreen px-4 py-3 text-left text-sm font-medium leading-5 text-white shadow-lg">
                  <div>{nightlyPriceText()}</div>
                  {basePrice && (
                    <div>{formatCents(basePrice)} total before tax/fees</div>
                  )}
                  {estimatedTax && (
                    <div>{formatCents(estimatedTax)} estimated tax</div>
                  )}
                  <div>
                    {totalPrice ? formatCents(totalPrice) : 'N/A'} total
                    including tax/fees
                  </div>
                </div>
              </PopoverPanel>
            )}
          </Popover>
          <p className="text-sm text-primaryGray sm:text-xs">per night</p>
        </div>

        <div className="flex flex-col text-right sm:mt-1 sm:text-right">
          <p className="text-sm font-medium text-gray-900 sm:text-sm sm:font-normal sm:text-primaryGray">
            Total: {basePrice ? formatCents(basePrice) : 'N/A'} {PLUS_TAX_TEXT}
          </p>
          <p className="text-sm text-primaryGray sm:text-xs">
            ({nights} {pluralize(nights, 'night')})
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceTag;
